import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { useState } from "react";

const platforms = [
  {
    tag: "PLATFORM 01",
    name: "Blude LifeX",
    role: "Therapeutic Interventions",
    desc: "Our technology platform focused on advanced therapeutic interventions - bridging cutting-edge R&D with rapid, deployable solutions for critical emergency settings.",
    points: [
      "Trauma and emergency formulations",
      "Hard-to-heal wound technologies",
      "Chronic therapeutic solutions",
      "Self-activating, adaptive systems",
    ],
    accent: "from-[#E60000] to-[#8B0000]",
  },
  {
    tag: "PLATFORM 02",
    name: "Blude TechX",
    role: "Research & Translational",
    desc: "Our research and translational platform supporting preclinical development, regulatory pathways, and collaborative scientific ventures for next-generation biomedical applications.",
    points: [
      "Preclinical research services",
      "Regulatory & translational support",
      "Collaborative industry partnerships",
      "Custom MedTech & software solutions",
    ],
    accent: "from-[#00A3FF] to-[#0066B5]",
  },
];

export const Platforms = () => {
  const [hover, setHover] = useState(null);

  return (
    <section
      id="platforms"
      data-testid="platforms-section"
      className="relative section-pad bg-[#0A0A0C]"
    >
      <div className="absolute inset-x-0 top-0 divider-grad" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-2 h-2 rounded-full bg-[#E60000]" />
          <span className="eyebrow">PRODUCT LINES · 005</span>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 font-display text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter font-medium text-white"
          >
            Two platforms.
            <br />
            One <span className="italic font-light">mission.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 text-base sm:text-lg text-blude-textDim self-end text-justify"
          >
            Two specialised platforms delivering science-backed solutions across
            therapy and research.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          {platforms.map((p, i) => (
            <motion.div
              key={p.tag}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: i * 0.15 }}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              className={`card-tile p-8 sm:p-10 min-h-[560px] flex flex-col transition-all duration-500 ${
                hover !== null && hover !== i ? "opacity-50" : "opacity-100"
              }`}
              data-testid={`platform-card-${i + 1}`}
            >
              <div
                className={`absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-gradient-to-br ${p.accent} opacity-[0.13] blur-3xl pointer-events-none`}
              />

              <div className="relative flex items-start justify-between">
                <div>
                  <span className="font-mono text-[10px] tracking-[0.3em] text-white/40">
                    {p.tag}
                  </span>
                  <h3 className="mt-4 font-display text-5xl sm:text-6xl text-white tracking-tighter">
                    {p.name.split(" ").map((w, idx) => (
                      <span
                        key={idx}
                        className={idx === 1 ? "italic font-light" : ""}
                      >
                        {idx > 0 ? " " : ""}
                        {w}
                      </span>
                    ))}
                  </h3>
                  <div className="mt-2 text-sm text-[#FF6A4D] tracking-wide">
                    {p.role}
                  </div>
                </div>
                <ArrowUpRight className="w-7 h-7 text-white/30" />
              </div>

              <p className="relative mt-7 text-[15px] text-blude-textDim leading-relaxed text-justify">
                {p.desc}
              </p>

              <ul className="relative mt-8 space-y-3">
                {p.points.map((pt) => (
                  <li
                    key={pt}
                    className="flex items-start gap-3 text-sm text-white/85"
                  >
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                      <Check className="w-3 h-3 text-[#FF2400]" />
                    </span>
                    {pt}
                  </li>
                ))}
              </ul>

              <div className="relative mt-auto pt-8 flex items-center justify-between text-[11px] font-mono tracking-widest text-white/40">
                <span>BLUDE · {p.name.toUpperCase()}</span>
                <span>{i === 0 ? "THERAPEUTIC" : "RESEARCH"}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
